import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import config from "../config";

const LAUNCH_AT = new Date(2026, 7, 24, 0, 0, 0, 0).getTime();
const END_AT = new Date(2026, 7, 31, 23, 59, 59, 999).getTime();
const REPEAT_MS = 5 * 60 * 60 * 1000;
const STORAGE_LAUNCHING = "cpLaunchBannerDismissed_launching_v5";

const getPortalLoginHref = () => {
  const configured = String(config.customerPortalPublicUrl || "").trim().replace(/\/+$/, "");
  if (configured) {
    return configured + "/login";
  }
  const hostname = window.location.hostname;
  if (hostname === "app.beautisoftware.com") {
    return "https://customer-portal.beautisoftware.com/login";
  }
  if (hostname === "staging.beautisoftware.com") {
    return "https://customer-portal-stg.beautisoftware.com/login";
  }
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return window.location.protocol + "//" + hostname + ":3001/login";
  }
  return "https://customer-portal.beautisoftware.com/login";
};

const shouldHideOnPath = (pathname) => {
  return (
    pathname.indexOf("/customer-portal") === 0 ||
    pathname.indexOf("/booking") === 0 ||
    pathname.indexOf("/consultationforms") === 0
  );
};

const getBannerPreview = () => {
  try {
    const host = window.location.hostname;
    const allowed = host === "localhost" || host === "127.0.0.1" || host.indexOf("staging") !== -1;
    if (!allowed) {
      return "";
    }
    return String(new URLSearchParams(window.location.search).get("cpBanner") || "").toLowerCase();
  } catch (error) {
    return "";
  }
};

const getPreviewNow = (preview) => {
  if (preview === "launching") {
    return LAUNCH_AT + 1000;
  }
  if (preview === "upcoming") {
    return Date.now();
  }
  if (preview === "ended") {
    return END_AT + 1000;
  }
  return Date.now();
};

const readDismissedAt = (key) => {
  try {
    const value = Number(window.localStorage.getItem(key) || 0);
    return Number.isFinite(value) && value > 0 ? value : 0;
  } catch (error) {
    return 0;
  }
};

const LAUNCH_STARS = [
  { top: "8%", left: "6%", size: 10, delay: "0s", duration: "1.5s", symbol: "✦" },
  { top: "14%", left: "88%", size: 13, delay: "0.35s", duration: "1.8s", symbol: "★" },
  { top: "22%", left: "18%", size: 8, delay: "0.7s", duration: "1.4s", symbol: "✧" },
  { top: "32%", left: "72%", size: 11, delay: "0.15s", duration: "2s", symbol: "✦" },
  { top: "48%", left: "8%", size: 12, delay: "0.9s", duration: "1.6s", symbol: "★" },
  { top: "54%", left: "92%", size: 9, delay: "0.5s", duration: "1.7s", symbol: "✧" },
  { top: "66%", left: "16%", size: 10, delay: "1.1s", duration: "1.9s", symbol: "✦" },
  { top: "74%", left: "78%", size: 14, delay: "0.25s", duration: "1.5s", symbol: "★" },
  { top: "84%", left: "28%", size: 8, delay: "0.8s", duration: "2.1s", symbol: "✧" },
  { top: "88%", left: "58%", size: 11, delay: "0.45s", duration: "1.6s", symbol: "✦" },
  { top: "40%", left: "46%", size: 7, delay: "1.3s", duration: "1.3s", symbol: "★" },
  { top: "6%", left: "48%", size: 9, delay: "0.6s", duration: "1.8s", symbol: "✧" },
];

const writeDismissedAt = (key) => {
  try {
    window.localStorage.setItem(key, String(Date.now()));
  } catch (error) {
    /* ignore storage errors */
  }
};

const CustomerPortalLaunchBanner = () => {
  const location = useLocation();
  const preview = getBannerPreview();
  const [now, setNow] = useState(() => getPreviewNow(preview));
  const [open, setOpen] = useState(true);
  const reopenTimerRef = useRef(null);
  const isUpcoming = now < LAUNCH_AT;
  const isEnded = now > END_AT;
  const dismissKey = STORAGE_LAUNCHING;

  useEffect(() => {
    setNow(getPreviewNow(getBannerPreview()));
  }, [location.search]);

  useEffect(() => {
    if (preview) {
      return undefined;
    }

    const timers = [];
    const msToLaunch = LAUNCH_AT - Date.now();
    const msToEnd = END_AT - Date.now();

    if (msToLaunch > 0) {
      timers.push(setTimeout(() => setNow(Date.now()), msToLaunch + 250));
    }
    if (msToEnd > 0) {
      timers.push(setTimeout(() => setNow(Date.now()), msToEnd + 250));
    }

    return () => timers.forEach((timer) => clearTimeout(timer));
  }, [preview]);

  useEffect(() => {
    if (isEnded) {
      setOpen(false);
      return undefined;
    }

    if (preview) {
      setOpen(true);
      return undefined;
    }

    const dismissedAt = readDismissedAt(dismissKey);
    if (!dismissedAt) {
      setOpen(true);
      return undefined;
    }

    const wait = REPEAT_MS - (Date.now() - dismissedAt);
    if (wait <= 0) {
      setOpen(true);
      return undefined;
    }

    setOpen(false);
    const timer = setTimeout(() => setOpen(true), wait);
    return () => clearTimeout(timer);
  }, [dismissKey, isEnded, location.pathname, preview]);

  useEffect(() => {
    return () => {
      if (reopenTimerRef.current) {
        clearTimeout(reopenTimerRef.current);
      }
    };
  }, []);

  if (isUpcoming || isEnded || !open || shouldHideOnPath(location.pathname || "")) {
    return null;
  }

  const closeBanner = () => {
    writeDismissedAt(dismissKey);
    setOpen(false);
    if (reopenTimerRef.current) {
      clearTimeout(reopenTimerRef.current);
    }
    reopenTimerRef.current = setTimeout(() => setOpen(true), REPEAT_MS);
  };

  return (
    <div
      id="customer-portal-launch-banner"
      className="cp-launch-banner cp-launch-banner--live"
      style={{
        position: "fixed",
        right: "20px",
        bottom: "20px",
        width: "400px",
        maxWidth: "calc(100vw - 32px)",
        zIndex: 2147483647,
        overflow: "hidden",
        borderRadius: "24px",
        color: "#fff",
        background: "linear-gradient(165deg, #c43a72 0%, #8c1c4d 42%, #5a1033 100%)",
        border: "1px solid rgba(243, 199, 122, 0.55)",
        boxShadow: "0 0 0 4px rgba(243, 199, 122, 0.12), 0 22px 48px rgba(107, 20, 59, 0.55), 0 0 36px rgba(243, 199, 122, 0.28)",
        fontFamily: "Source Sans Pro, Segoe UI, Arial, sans-serif",
      }}
    >
      <style>
        {`
          @keyframes cp-star-sparkle {
            0%, 100% { opacity: 0.15; transform: translateY(0) scale(0.55) rotate(0deg); }
            50% { opacity: 1; transform: translateY(-8px) scale(1.25) rotate(18deg); }
          }
          @keyframes cp-shimmer {
            0% { transform: translateX(-130%) skewX(-18deg); }
            100% { transform: translateX(230%) skewX(-18deg); }
          }
          @keyframes cp-badge-pulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(243, 199, 122, 0.55); transform: scale(1); }
            50% { box-shadow: 0 0 0 8px rgba(243, 199, 122, 0); transform: scale(1.04); }
          }
          @keyframes cp-cta-shine {
            0% { left: -40%; }
            100% { left: 140%; }
          }
          @keyframes cp-glow {
            0%, 100% { opacity: 0.45; }
            50% { opacity: 0.9; }
          }
          .cp-launch-banner--live .cp-launch-cta {
            position: relative;
            overflow: hidden;
            background: linear-gradient(90deg, #fff7e6 0%, #f3c77a 50%, #fff 100%);
            color: #6b143b;
            box-shadow: 0 8px 18px rgba(0,0,0,0.18);
          }
          .cp-launch-banner--live .cp-launch-cta:hover {
            color: #5a1033;
            filter: brightness(1.05);
          }
          .cp-launch-banner--live .cp-launch-cta::after {
            content: "";
            position: absolute;
            top: 0;
            left: -40%;
            width: 30%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255,255,255,0.7), transparent);
            animation: cp-cta-shine 2.4s ease-in-out infinite;
          }
        `}
      </style>
      <>
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              top: "-40px",
              right: "-30px",
              width: "140px",
              height: "140px",
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(243,199,122,0.45) 0%, rgba(243,199,122,0) 70%)",
              animation: "cp-glow 2.8s ease-in-out infinite",
              pointerEvents: "none",
            }}
          />
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              bottom: "40px",
              left: "-36px",
              width: "120px",
              height: "120px",
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 70%)",
              animation: "cp-glow 3.4s ease-in-out infinite",
              pointerEvents: "none",
            }}
          />
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "40%",
              height: "100%",
              background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent)",
              animation: "cp-shimmer 3.6s ease-in-out infinite",
              pointerEvents: "none",
              zIndex: 1,
            }}
          />
          {LAUNCH_STARS.map((star, index) => (
            <span
              key={index}
              aria-hidden="true"
              style={{
                position: "absolute",
                top: star.top,
                left: star.left,
                zIndex: 2,
                pointerEvents: "none",
                color: index % 3 === 0 ? "#fff" : "#f3c77a",
                fontSize: star.size + "px",
                lineHeight: 1,
                textShadow: "0 0 10px rgba(243, 199, 122, 1)",
                animation: "cp-star-sparkle " + star.duration + " ease-in-out " + star.delay + " infinite",
              }}
            >
              {star.symbol}
            </span>
          ))}
        </>
      <button
        type="button"
        onClick={closeBanner}
        aria-label="Close"
        style={{
          position: "absolute",
          top: "10px",
          right: "10px",
          width: "28px",
          height: "28px",
          border: 0,
          borderRadius: "50%",
          background: "rgba(32, 20, 28, 0.55)",
          color: "#fff",
          cursor: "pointer",
          fontSize: "16px",
          lineHeight: "28px",
          zIndex: 4,
        }}
      >
        ×
      </button>
      <div style={{ position: "relative", zIndex: 3, padding: "12px 12px 0" }}>
        <a href={getPortalLoginHref()} target="_blank" rel="noopener noreferrer" style={{ display: "block" }}>
          <img
            src={config.imagepath + "customer-portal-launch.png"}
            alt="Customer Portal login"
            style={{
              display: "block",
              width: "100%",
              height: "158px",
              objectFit: "cover",
              objectPosition: "center top",
              background: "#f7f4f2",
              borderRadius: "16px",
              border: "2px solid rgba(243, 199, 122, 0.7)",
              boxShadow: "0 8px 20px rgba(0,0,0,0.22)",
            }}
          />
        </a>
      </div>
      <div style={{ position: "relative", zIndex: 3, padding: "16px 18px 18px" }}>
        <div
          style={{
            display: "inline-block",
            marginBottom: "10px",
            padding: "5px 12px",
            borderRadius: "999px",
            background: "linear-gradient(90deg, #ffe7b0, #f3c77a)",
            color: "#5c3b12",
            fontSize: "11px",
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            animation: "cp-badge-pulse 1.8s ease-in-out infinite",
          }}
        >
          ✦ Launching now
        </div>
        <h4 style={{ margin: "0 0 8px", fontSize: "23px", fontWeight: 700, letterSpacing: "-0.02em" }}>
          Customer Portal is live
        </h4>
        <p style={{ margin: "0 0 16px", fontSize: "14px", lineHeight: 1.55, color: "rgba(255,255,255,0.92)" }}>
          Your clients can now sign in to view appointments, manage upcoming bookings, and access treatment and invoice history.
        </p>
        <a
          className="cp-launch-cta"
          href={getPortalLoginHref()}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "block",
            width: "100%",
            boxSizing: "border-box",
            textAlign: "center",
            padding: "12px 14px",
            borderRadius: "12px",
            fontWeight: 700,
            textDecoration: "none",
          }}
        >
          Open Customer Portal
        </a>
        <a
          href={(config.basePath || "") + "/settings?tab=CustomerPortal"}
          style={{
            display: "block",
            marginTop: "10px",
            textAlign: "center",
            color: "rgba(255, 231, 176, 0.95)",
            fontSize: "13px",
          }}
        >
          Enable it in Settings
        </a>
      </div>
    </div>
  );
};

export default CustomerPortalLaunchBanner;
